import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatEventTime } from '@/lib/eventTime';
import { startOfMonth, startOfWeek, addDays, sameDay, isToday, itemsForDay } from './calendarItems';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ItemChip({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onSelect(item); }}
      title={item.title}
      data-testid={`month-item-${item.key}`}
      className="group flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[11px] hover:bg-slate-100 transition"
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.theme.dot}`} />
      {!item.allDay && (
        <span className="shrink-0 tabular-nums text-slate-500">
          {formatEventTime(item.start.toISOString(), item.raw?.timezone).replace(/:00/, '')}
        </span>
      )}
      <span className="truncate text-slate-700 group-hover:text-slate-900">{item.title}</span>
    </button>
  );
}

export default function MonthView({ items, cursor, onSelect }) {
  const [dayModal, setDayModal] = useState(null);

  const cells = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(cursor));
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [cursor]);

  const month = cursor.getMonth();

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="calendar-month-view">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <span className="hidden sm:inline">{d}</span>
            <span className="sm:hidden">{d[0]}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          const dayItems = itemsForDay(items, day);
          const inMonth = day.getMonth() === month;
          const today = isToday(day);
          const visible = dayItems.slice(0, 3);
          const extra = dayItems.length - visible.length;
          return (
            <div
              key={idx}
              className={`min-h-[96px] border-b border-r border-slate-100 p-1.5 ${inMonth ? 'bg-white' : 'bg-slate-50/60'} ${idx % 7 === 6 ? 'border-r-0' : ''}`}
              data-testid={`month-cell-${day.toISOString().slice(0, 10)}`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-medium ${
                    today ? 'bg-slate-900 text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'
                  }`}
                >
                  {day.getDate()}
                </span>
              </div>
              <div className="space-y-0.5">
                {visible.map((it) => <ItemChip key={it.key} item={it} onSelect={onSelect} />)}
                {extra > 0 && (
                  <button
                    type="button"
                    onClick={() => setDayModal(day)}
                    className="w-full rounded px-1 text-left text-[11px] font-medium text-slate-500 hover:text-slate-800"
                    data-testid={`month-more-${day.toISOString().slice(0, 10)}`}
                  >
                    + {extra} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
        <DialogContent className="max-w-md" data-testid="month-day-modal">
          <DialogHeader>
            <DialogTitle>
              {dayModal?.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-1 overflow-y-auto">
            {dayModal && itemsForDay(items, dayModal).map((it) => (
              <ItemChip key={it.key} item={it} onSelect={(i) => { setDayModal(null); onSelect(i); }} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
